import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import type { DataTableLoadExtraRow } from '@customerio/pluma-components/react';
import {
  countNewItems,
  loadNewLabel,
  newItemsAnnouncement,
  type NewItemsCount,
  type NewItemsNoun,
} from '@/utils/new-items';

const NO_NEW_ITEMS: NewItemsCount = { count: 0, hasMore: false };

export interface UseLoadNewInput {
  /** The ids the table shows. */
  loadedIds: ReadonlySet<string>;
  /** The ids on the check's latest page, and whether that page has a next cursor. */
  latestIds: readonly string[];
  latestHasMore: boolean;
  /** The noun for the entries, e.g. `{ one: 'message', other: 'messages' }`. */
  noun: NewItemsNoun;
  list: {
    /** Epoch millis of the list's last successful load. */
    lastUpdated: number;
    isLoading: boolean;
    isFetching: boolean;
    isFetchingMore: boolean;
    /** Refetches every loaded page of the list. */
    refetch: () => Promise<void>;
  };
  check: {
    /** Epoch millis of the check's last successful read. */
    dataUpdatedAt: number;
    refetch: () => Promise<void>;
  };
}

/** What the toolbar's "Checked … ago" status needs (see `components/new-items-status.tsx`). */
export interface NewItemsStatusState {
  /** Epoch millis of the last completed check or list load (`0` ⇒ not loaded yet → em dash). */
  lastChecked: number;
  /** True while the user's own check or a list load runs. Background checks do not count. */
  isChecking: boolean;
  /** Checks now and refetches the list, so rows on screen also get their latest state. */
  onCheckNow: () => void;
  /** For a polite live region: "3 new messages available", or '' when there are none. */
  announcement: string;
}

/**
 * The table's "Load N new …" row, typed for the React DataTable. Its click needs no event, so
 * `onClick` takes none; that still fits the DataTable prop.
 */
export type LoadNewRow = Omit<DataTableLoadExtraRow<MouseEvent>, 'onClick'> & { onClick: () => void };

export interface UseLoadNewResult {
  /** The table's "Load N new …" row. */
  loadNew: LoadNewRow;
  status: NewItemsStatusState;
  newItems: NewItemsCount;
}

/**
 * The "load new" row and the toolbar status for a newest-first feed, built from a list query and
 * its background check (see `services/db/shared/new-items-check.ts`).
 *
 * - The row counts the checked page's entries that the table does not show. Clicking it refetches
 *   the list. After "load more", that refetches every loaded page in turn: correct, but one request
 *   per page.
 * - When the table is empty, new entries load without a click: there is nothing on screen to move.
 * - The spinner shows only for the user's own check or a list load, not for the background check
 *   every 15s.
 */
export function useLoadNew({
  loadedIds,
  latestIds,
  latestHasMore,
  noun,
  list,
  check,
}: UseLoadNewInput): UseLoadNewResult {
  // A list at least as new as the check already has everything the check could offer; entries on
  // the check's page that are missing from it have left the list (e.g. a queued delivery was sent).
  // So count nothing until the next check.
  const isCheckStale = check.dataUpdatedAt <= list.lastUpdated;
  const newItems = isCheckStale ? NO_NEW_ITEMS : countNewItems(latestIds, loadedIds, latestHasMore);
  const isRefetchingList = list.isFetching && !list.isFetchingMore && !list.isLoading;
  const [isManualCheck, setIsManualCheck] = useState(false);

  const { refetch: refetchList } = list;
  const { refetch: refetchCheck } = check;

  const onCheckNow = useCallback(() => {
    setIsManualCheck(true);
    void Promise.all([refetchCheck(), refetchList()]).finally(() => setIsManualCheck(false));
  }, [refetchCheck, refetchList]);

  // An empty table loads new entries at once. Keyed by the check's time, so one check result
  // triggers at most one load, even if that load still comes back empty.
  const autoLoadedCheckRef = useRef(0);
  const shouldAutoLoad =
    loadedIds.size === 0 &&
    newItems.count > 0 &&
    !list.isLoading &&
    !list.isFetching &&
    autoLoadedCheckRef.current !== check.dataUpdatedAt;
  useEffect(() => {
    if (!shouldAutoLoad) {
      return;
    }
    autoLoadedCheckRef.current = check.dataUpdatedAt;
    void refetchList();
  }, [shouldAutoLoad, check.dataUpdatedAt, refetchList]);

  const loadNew = useMemo<LoadNewRow>(
    () => ({
      canLoad: newItems.count > 0,
      onClick: () => void refetchList(),
      isLoading: isRefetchingList,
      label: loadNewLabel({ count: newItems.count, hasMore: newItems.hasMore }, noun),
    }),
    [newItems.count, newItems.hasMore, noun, refetchList, isRefetchingList],
  );

  const status: NewItemsStatusState = {
    // The list load counts as a check too: after "load new", the list is the newest read.
    lastChecked: Math.max(check.dataUpdatedAt, list.lastUpdated),
    isChecking: isManualCheck || isRefetchingList,
    onCheckNow,
    announcement: newItemsAnnouncement(newItems, noun),
  };

  return { loadNew, status, newItems };
}
